import {
  normalizeWidgetTheme,
  APIURL,
  cancelSelectEvent,
  changeSymbolForWidget,
  getCSSStyles,
  getEChartsTooltipTheme,
  getSymbolDropdown,
  getTraderSMCardHTML,
  getWidgetDataURI,
  apiGet,
  toggleWidgetTheme,
  getHeaderPinStateClass,
  getHeaderPinButtonHTML,
  setupHeaderPin
} from '../../helpers';
import {
  InsiderSecuritiesTransactedVsPriceConfig,
  WidgetEvent,
  WidgetEventHandler,
  WidgetEventType
} from '../../types';

interface InsiderSecuritiesTransaction {
  symbol: string;
  transaction_date: string;
  filing_date: string;
  securities_transacted: number;
  price: number;
  transaction_amount: number;
  reporting_name: string;
  acquisition_or_disposition?: string;
}

interface InsiderSecuritiesScatterData {
  x: number[];
  y: number[];
  reporting_names: string[];
  symbols: string[];
  transaction_dates: string[];
  labels?: string[];
}

interface InsiderSecuritiesCategoryData {
  count: number;
  scatter_data: InsiderSecuritiesScatterData;
  data: InsiderSecuritiesTransaction[];
}

interface InsiderSecuritiesApiData {
  parameters: {
    symbol: string | null;
    days_back: number;
    min_transaction_value: number;
    show_top_n: number;
    n_largest_trades: number;
    cutoff_date: string;
    query_date: string;
  };
  summary_statistics: {
    total_acquisitions: number;
    total_dispositions: number;
    total_transactions: number;
    avg_price: number;
    min_price: number;
    max_price: number;
    avg_securities: number;
    min_securities: number;
    max_securities: number;
    total_value: number;
  };
  acquisitions: InsiderSecuritiesCategoryData;
  dispositions: InsiderSecuritiesCategoryData;
  largest_trades: InsiderSecuritiesCategoryData;
}

interface ScatterPoint {
  value: [number, number];
  ticker: string;
  insider: string;
  date: string;
  price: number;
  securities: number;
  transactionAmount: number;
}

const ACQUISITION_COLOR = '#33D17A';
const DISPOSITION_COLOR = '#FF5A5A';
const LARGEST_TRADE_COLOR = '#F5D020';
const STAR_SYMBOL = 'path://M50 5 L61 35 L95 35 L68 57 L79 91 L50 70 L21 91 L32 57 L5 35 L39 35 Z';

export class TraderverseTopNInsiderSecuritiesTransactedVsPrice {
  private container: HTMLElement;
  private config: InsiderSecuritiesTransactedVsPriceConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private shadowRoot: ShadowRoot;
  private apiData: InsiderSecuritiesApiData | null = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private filters = {
    symbol: '' as string | undefined,
    daysBack: 90 as number | '',
    minTransactionValue: 500000 as number | '',
    showTopN: 10 as number | '',
    nLargestTrades: 5 as number | ''
  };

  constructor(config: InsiderSecuritiesTransactedVsPriceConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.applyConfigFilters();
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private getDefaultConfig(): Partial<InsiderSecuritiesTransactedVsPriceConfig> {
    return {
      headerPinned: true,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      isSymbolSelectable: true,
      symbol: 'AAPL',
      daysBack: 90,
      minTransactionValue: 500000,
      showTopN: 10,
      nLargestTrades: 5
    };
  }

  private applyConfigFilters(): void {
    if (this.config.symbol) this.filters.symbol = this.config.symbol;
    if (typeof this.config.daysBack === 'number') this.filters.daysBack = this.config.daysBack;
    if (typeof this.config.minTransactionValue === 'number') {
      this.filters.minTransactionValue = this.config.minTransactionValue;
    }
    if (typeof this.config.showTopN === 'number') this.filters.showTopN = this.config.showTopN;
    if (typeof this.config.nLargestTrades === 'number') {
      this.filters.nLargestTrades = this.config.nLargestTrades;
    }
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    await this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    await this.fetchData();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private async createWidget(): Promise<void> {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
      themeClass = 'defaultLightMode';
      dataTheme = 'default-lite';
    }

    const widgetHtml = `
      <div style="width: ${typeof this.config.width === 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height === 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} Top N Insider Securities Transacted Vs Price</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
            <i class="pi pi-ellipsis-v"></i>
          </div>
        </div>
        <div class="inBody">
          <div class="optionsRow">
            ${this.config.hideSymbol ? '' : `<div class="item dropStyle01">
              <div class="trader-sm-card" id="selected-symbol">
                ${this.config.symbol
                  ? getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol, type: 'stock', name: '' })
                  : '<span>Select Symbol</span>'}
              </div>
              <span id="symbol-dropdown-placeholder"></span>
            </div>`}
            <div class="item">
              <label>Days Back</label>
              <input type="number" class="filter-days-back" min="1" step="1" placeholder="e.g. 90" value="${this.filters.daysBack}" />
            </div>
            <div class="item">
              <label>Min Transaction Value</label>
              <input type="number" class="filter-min-transaction-value" min="0" step="1" placeholder="e.g. 500000" value="${this.filters.minTransactionValue}" />
            </div>
            <div class="item">
              <label>Show Top N</label>
              <input type="number" class="filter-show-top-n" min="1" step="1" placeholder="e.g. 10" value="${this.filters.showTopN}" />
            </div>
            <div class="item">
              <label>N Largest Trades</label>
              <input type="number" class="filter-n-largest-trades" min="1" step="1" placeholder="e.g. 5" value="${this.filters.nLargestTrades}" />
            </div>
            <span></span>
            <div class="item">
              <div class="legendsCollection" id="insider-securities-legend">
                <span data-tooltip="Acquisitions" style="background-color:${ACQUISITION_COLOR};"></span>
                <span data-tooltip="Dispositions" style="background-color:${DISPOSITION_COLOR};"></span>
                <span data-tooltip="Largest Trades" style="background-color:${LARGEST_TRADE_COLOR};"></span>
              </div>
            </div>
          </div>
          <div class="chartDv insiderSecuritiesChart" id="insiderSecuritiesContainer">
            <div id="insiderSecuritiesChart" class="chart coverDv"></div>
          </div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    await this.attachStyles();
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) placeholder.replaceWith(dropdown);
    }

    const updateNumericFiltersAndFetch = () => {
      this.filters.daysBack = this.readNumberInput('.filter-days-back');
      this.filters.minTransactionValue = this.readNumberInput('.filter-min-transaction-value');
      this.filters.showTopN = this.readNumberInput('.filter-show-top-n');
      this.filters.nLargestTrades = this.readNumberInput('.filter-n-largest-trades');
      if (this.filters.daysBack !== '') this.config.daysBack = this.filters.daysBack;
      if (this.filters.minTransactionValue !== '') this.config.minTransactionValue = this.filters.minTransactionValue;
      if (this.filters.showTopN !== '') this.config.showTopN = this.filters.showTopN;
      if (this.filters.nLargestTrades !== '') this.config.nLargestTrades = this.filters.nLargestTrades;
      this.fetchData();
    };

    [
      '.filter-days-back',
      '.filter-min-transaction-value',
      '.filter-show-top-n',
      '.filter-n-largest-trades'
    ].forEach((selector) => {
      const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
      input?.addEventListener('change', updateNumericFiltersAndFetch);
    });
  }

  private readNumberInput(selector: string): number | '' {
    const input = this.shadowRoot.querySelector(selector) as HTMLInputElement | null;
    if (!input || input.value.trim() === '') return '';
    const value = parseFloat(input.value);
    return Number.isNaN(value) ? '' : value;
  }

  private onSelectChange(stock: any): void {
    this.changeSymbol(stock.symbol, stock);
  }

  private buildQueryParams(): Record<string, string | number> {
    const query: Record<string, string | number> = {};
    if (this.filters.symbol) query.symbol = this.filters.symbol;
    if (this.filters.daysBack !== '') query.daysBack = this.filters.daysBack;
    if (this.filters.minTransactionValue !== '') query.minTransactionValue = this.filters.minTransactionValue;
    if (this.filters.showTopN !== '') query.showTopN = this.filters.showTopN;
    if (this.filters.nLargestTrades !== '') query.nLargestTrades = this.filters.nLargestTrades;
    return query;
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
      script.async = true;
      script.onload = () => resolve(true);
      this.shadowRoot.appendChild(script);
    });
  }

  private parseApiPayload(json: unknown): InsiderSecuritiesApiData | null {
    if (!json || typeof json !== 'object') return null;
    const envelope = json as { isSuccess?: boolean; data?: InsiderSecuritiesApiData };
    if (envelope.isSuccess === false) return null;
    return (envelope.data ?? json) as InsiderSecuritiesApiData;
  }

  private hasChartData(data: InsiderSecuritiesApiData | null): boolean {
    if (!data) return false;
    return (
      (data.acquisitions?.count ?? 0) > 0 ||
      (data.dispositions?.count ?? 0) > 0 ||
      (data.largest_trades?.count ?? 0) > 0
    );
  }

  private buildScatterPoints(
    category: InsiderSecuritiesCategoryData | undefined
  ): ScatterPoint[] {
    if (!category?.scatter_data) return [];

    const { scatter_data: scatter, data: details } = category;
    return scatter.x.map((securities, index) => {
      const price = scatter.y[index] ?? 0;
      const detail = details[index];
      return {
        value: [price, securities],
        ticker: scatter.symbols[index] ?? detail?.symbol ?? '',
        insider: scatter.reporting_names[index] ?? detail?.reporting_name ?? '',
        date: scatter.transaction_dates[index] ?? detail?.transaction_date ?? '',
        price: detail?.price ?? price,
        securities: detail?.securities_transacted ?? securities,
        transactionAmount: detail?.transaction_amount ?? price * securities
      };
    });
  }

  private async fetchData(symbol?: string): Promise<void> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const container = this.shadowRoot.querySelector('#insiderSecuritiesContainer') as HTMLElement;
    if (widget) widget.classList.add('loading');

    if (symbol) {
      this.filters.symbol = symbol;
      this.config.symbol = symbol;
    }

    try {
      const res = await apiGet({
        method: 'GET',
        url: `${APIURL}/v1/equity-market/insider-securities`,
        query: this.buildQueryParams(),
        headers: { }
      });
      if (!res.ok) throw new Error('Failed to fetch insider securities data');

      const json = await res.json();
      this.apiData = this.parseApiPayload(json);

      if (!this.hasChartData(this.apiData)) {
        if (this.currentChart) {
          this.currentChart.dispose();
          this.currentChart = null;
        }
        const chartEl = this.shadowRoot.querySelector('#insiderSecuritiesChart') as HTMLElement;
        if (chartEl) chartEl.innerHTML = '';
        if (container) {
          container.innerHTML = '<div class="noData"><p>No data available</p></div>';
        }
      } else {
        if (container && !container.querySelector('#insiderSecuritiesChart')) {
          container.innerHTML = '<div id="insiderSecuritiesChart" class="chart coverDv"></div>';
        }
        this.drawChart();
      }

      this.emit('dataUpdate', this.apiData);
    } catch (err) {
      console.error('Failed to fetch insider securities data:', err);
      this.apiData = null;
      if (container) container.innerHTML = '<div class="noData"><p>Failed to load data</p></div>';
      this.emit('error', err);
    } finally {
      if (widget) widget.classList.remove('loading');
    }
  }

  private formatCurrency(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 1e12) return `$ ${(value / 1e12).toLocaleString(undefined, { maximumFractionDigits: 2 })}T`;
    if (abs >= 1e9) return `$ ${(value / 1e9).toLocaleString(undefined, { maximumFractionDigits: 2 })}B`;
    if (abs >= 1e6) return `$ ${(value / 1e6).toLocaleString(undefined, { maximumFractionDigits: 2 })}M`;
    if (abs >= 1e3) return `$ ${(value / 1e3).toLocaleString(undefined, { maximumFractionDigits: 2 })}K`;
    return `$ ${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }

  private formatAxisValue(value: number): string {
    if (value === 0) return '0';
    const abs = Math.abs(value);
    if (abs >= 1e9) {
      const billions = value / 1e9;
      return billions % 1 === 0 ? `${billions}B` : `${billions.toFixed(1)}B`;
    }
    if (abs >= 1e6) {
      const millions = value / 1e6;
      return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
    }
    if (abs >= 1e3) return `${Math.round(value / 1e3)}K`;
    return `${Math.round(value)}`;
  }

  private getNiceAxisMax(maxValue: number): number {
    if (maxValue <= 0) return 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
    const normalized = maxValue / magnitude;
    const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
    return niceNormalized * magnitude;
  }

  private buildTooltipHtml(point: ScatterPoint, markerColor: string, isStar = false): string {
    const marker = isStar
      ? `<span style="display:inline-block;width:12px;height:12px;background:${LARGEST_TRADE_COLOR};clip-path:polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%);margin-right:6px;"></span>`
      : `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${markerColor};margin-right:6px;"></span>`;

    return `
      <div style="min-width:200px">
        <div style="display:flex;align-items:center;font-weight:600;margin-bottom:8px;font-size:14px;">
          ${marker}${this.escapeHtml(point.ticker)}
        </div>
        <div style="font-size:18px;font-weight:700;margin-bottom:10px;">${this.formatCurrency(point.transactionAmount)}</div>
        <div style="line-height:1.6">
          <div>Insider: <b>${this.escapeHtml(point.insider)}</b></div>
          <div>Symbol: <b>${this.escapeHtml(point.ticker)}</b></div>
          <div>Date: <b>${this.escapeHtml(point.date)}</b></div>
          <div>Price: <b>${this.formatCurrency(point.price)}</b></div>
        </div>
      </div>`;
  }

  private updateDisplay(): void {
    if (!this.hasChartData(this.apiData) || !(window as any).echarts) return;
    this.drawChart();
  }

  private drawChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#insiderSecuritiesChart') as HTMLElement;
    if (!container || !this.hasChartData(this.apiData)) return;

    if (this.currentChart) {
      this.currentChart.dispose();
    }

    const acquisitions = this.buildScatterPoints(this.apiData!.acquisitions);
    const dispositions = this.buildScatterPoints(this.apiData!.dispositions);
    const largestTrades = this.buildScatterPoints(this.apiData!.largest_trades);

    const allPoints = [...acquisitions, ...dispositions, ...largestTrades];
    const priceValues = allPoints.map((p) => p.value[0]);
    const securitiesValues = allPoints.map((p) => p.value[1]);
    const maxPrice = this.getNiceAxisMax(Math.max(...priceValues, 0));
    const maxSecurities = this.getNiceAxisMax(Math.max(...securitiesValues, 0));

    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color').trim() || '#94A3B8';
    const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color').trim() || 'rgba(148, 163, 184, 0.25)';
    const tooltipTheme = getEChartsTooltipTheme(widgetBox);

    const chart = echarts.init(container);
    chart.setOption({
      backgroundColor: 'transparent',
      tooltip: {
        ...tooltipTheme,
        trigger: 'item',
        formatter: (params: any) => {
          const point = params.data as ScatterPoint;
          if (!point?.ticker) return '';
          if (params.seriesName === 'Largest Trades') {
            return this.buildTooltipHtml(point, LARGEST_TRADE_COLOR, true);
          }
          if (params.seriesName === 'Acquisitions') {
            return this.buildTooltipHtml(point, ACQUISITION_COLOR);
          }
          return this.buildTooltipHtml(point, DISPOSITION_COLOR);
        }
      },
      grid: {
        left: 56,
        right: 24,
        top: 24,
        bottom: 48,
        containLabel: false
      },
      xAxis: {
        type: 'value',
        name: 'Price',
        nameLocation: 'middle',
        nameGap: 30,
        min: 0,
        max: maxPrice,
        nameTextStyle: { color: textColor, fontSize: 11 },
        axisLabel: {
          color: textColor,
          formatter: (value: number) => this.formatAxisValue(value)
        },
        splitLine: {
          show: true,
          lineStyle: { color: splitLineColor, type: 'dotted' }
        },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      yAxis: {
        type: 'value',
        name: 'Securities',
        nameLocation: 'middle',
        nameGap: 42,
        min: 0,
        max: maxSecurities,
        nameTextStyle: { color: textColor, fontSize: 11 },
        axisLabel: {
          color: textColor,
          formatter: (value: number) => this.formatAxisValue(value)
        },
        splitLine: {
          show: true,
          lineStyle: { color: splitLineColor, type: 'dotted' }
        },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      series: [
        {
          name: 'Acquisitions',
          type: 'scatter',
          data: acquisitions,
          symbolSize: 10,
          itemStyle: { color: ACQUISITION_COLOR, opacity: 0.9 },
          emphasis: { scale: 1.4 }
        },
        {
          name: 'Dispositions',
          type: 'scatter',
          data: dispositions,
          symbolSize: 10,
          itemStyle: { color: DISPOSITION_COLOR, opacity: 0.9 },
          emphasis: { scale: 1.4 }
        },
        {
          name: 'Largest Trades',
          type: 'scatter',
          data: largestTrades,
          symbol: STAR_SYMBOL,
          symbolSize: 14,
          itemStyle: { color: LARGEST_TRADE_COLOR, opacity: 1 },
          emphasis: { scale: 1.4 }
        }
      ]
    });

    this.currentChart = chart;
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (symbol: string) => this.fetchData(symbol),
      (type: any, data: any) => this.emit(type, data)
    );
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter((h) => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
    this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    if (this.currentChart) {
      try { this.currentChart.dispose(); } catch { /* ignore */ }
      this.currentChart = null;
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach((h) => h(evt));
    }
  }

  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/><path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/></svg>`;
  }
}

declare global {
  interface Window {
    TraderverseTopNInsiderSecuritiesTransactedVsPrice: typeof TraderverseTopNInsiderSecuritiesTransactedVsPrice;
  }
}
if (typeof window !== 'undefined') {
  window.TraderverseTopNInsiderSecuritiesTransactedVsPrice = TraderverseTopNInsiderSecuritiesTransactedVsPrice;
}
